/**
 * Normalized storefront product model (display + cart wiring).
 * Prices/stock from here are for UI only; checkout uses backend recomputation.
 */

import { normalizeAttributes } from './attributes'

/** Minimal category node for navigation / PDP context. */
export interface NormalizedCategory {
  id: string
  name: string | null
  slug: string | null
}

export interface NormalizedVariant {
  id: string
  productId: string
  attributes: Record<string, string>
  price: number | null
  /** Display hints only — backend enforces real availability. */
  stock: number | null
  inventoryQuantity: number | null
  status: 'active' | 'inactive' | 'out_of_stock' | 'archived'
  isDefault: boolean
  sku: string | null
  imageUrl: string | null
}

export interface NormalizedProduct {
  id: string
  storeId: string
  name: string
  description: string | null
  slug: string | null
  images: string[]
  hasVariants: boolean
  variants: NormalizedVariant[]
  defaultVariantId: string | null
  /** Base price when no variants or for range display; variable products may use variant prices. */
  price: number
  /** Base stock when simple; for variable, often aggregate — display only. */
  stock: number
  trackInventory: boolean
  isDropshipping: boolean
  status: 'active' | 'out_of_stock' | 'draft'
  category: NormalizedCategory | null
  /** Derived product-type flags */
  productType: string | null
  isDigital: boolean
  isService: boolean
  /** isLocal = not dropshipping and not digital and not service */
  isLocal: boolean
  /** needsShipping = physical item (local or dropshipping) */
  needsShipping: boolean
  /** instantDelivery = digital products delivered immediately */
  instantDelivery: boolean
  importSource: string | null
  digitalFileUrl: string | null
  compareAtPrice: number | null
}

/**
 * Raw shapes from Supabase/API (snake_case). Intentionally loose — normalization validates/coerces.
 */
export interface RawStorefrontVariant {
  id: string
  product_id?: string
  attributes?: Record<string, string> | null
  price?: number | null
  stock?: number | null
  inventory_quantity?: number | null
  status?: string | null
  is_default?: boolean | null
  sku?: string | null
  image_url?: string | null
}

export interface RawStorefrontProduct {
  id: string
  store_id: string
  name: string
  description?: string | null
  meta_description?: string | null
  slug?: string | null
  image?: string | null
  images?: string[] | null
  product_images?: { url: string; position?: number }[] | null
  has_variants?: boolean | null
  variants?: RawStorefrontVariant[] | null
  default_variant_id?: string | null
  price?: number | null
  compare_at_price?: number | null
  stock?: number | null
  track_inventory?: boolean | null
  is_dropshipping?: boolean | null
  status?: string | null
  category_id?: string | null
  /** When joined from categories table */
  categories?: { id: string; name: string; slug: string } | null
  product_type?: string | null
  digital_file_url?: string | null
  import_source?: string | null
  /** Optional size chart (demo / extended payloads) */
  size_chart?: {
    type: 'clothing' | 'shoes'
    values: Record<string, string>[]
  } | null
}

function coerceStatus(
  s: string | null | undefined
): 'active' | 'out_of_stock' | 'draft' {
  if (s === 'out_of_stock' || s === 'draft') return s
  return 'active'
}

function coerceVariantStatus(
  s: string | null | undefined
): NormalizedVariant['status'] {
  if (s === 'inactive' || s === 'out_of_stock' || s === 'archived') return s
  return 'active'
}

function collectImages(raw: RawStorefrontProduct): string[] {
  const out: string[] = []
  if (raw.product_images?.length) {
    const sorted = [...raw.product_images].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0)
    )
    for (const row of sorted) {
      if (row.url) out.push(row.url)
    }
  }
  if (out.length === 0 && raw.images?.length) {
    out.push(...raw.images.filter(Boolean))
  }
  if (raw.image && !out.includes(raw.image)) {
    out.unshift(raw.image)
  }
  return out
}

function normalizeVariantRow(raw: RawStorefrontVariant, productId: string): NormalizedVariant {
  return {
    id: raw.id,
    productId: raw.product_id ?? productId,
    attributes: normalizeAttributes((raw.attributes ?? {}) as Record<string, string>),
    price: raw.price != null && Number.isFinite(Number(raw.price)) ? Number(raw.price) : null,
    stock: raw.stock != null ? Number(raw.stock) : null,
    inventoryQuantity:
      raw.inventory_quantity != null ? Number(raw.inventory_quantity) : null,
    status: coerceVariantStatus(raw.status),
    isDefault: raw.is_default === true,
    sku: raw.sku ?? null,
    imageUrl: raw.image_url ?? null,
  }
}

/**
 * Map API/DB product into a single storefront shape.
 * Does not infer missing defaults beyond coercions; invalid variable products surface via resolveVariant.
 */
export function normalizeStorefrontProduct(raw: RawStorefrontProduct): NormalizedProduct {
  const hasVariants = raw.has_variants === true
  const variants = (raw.variants ?? []).map((v) => normalizeVariantRow(v, raw.id))
  const description =
    raw.description != null && String(raw.description).trim() !== ''
      ? String(raw.description)
      : raw.meta_description != null
        ? String(raw.meta_description)
        : null

  let category: NormalizedCategory | null = null
  if (raw.categories) {
    category = {
      id: raw.categories.id,
      name: raw.categories.name ?? null,
      slug: raw.categories.slug ?? null,
    }
  } else if (raw.category_id) {
    category = { id: raw.category_id, name: null, slug: null }
  }

  const productType = raw.product_type ?? null
  const isDropshipping =
    raw.is_dropshipping === true ||
    String(productType ?? '').toLowerCase() === 'dropshipping' ||
    String(raw.import_source ?? '').toLowerCase() === 'aliexpress' ||
    (typeof raw.supplier_url === 'string' && raw.supplier_url.trim() !== '')
  const isDigital = productType === 'digital' || Boolean(raw.digital_file_url)
  const isService = productType === 'service'
  const isLocal = !isDropshipping && !isDigital && !isService
  const needsShipping = (isLocal || isDropshipping) && !isDigital && !isService
  const instantDelivery = isDigital

  const capRaw = raw.compare_at_price != null ? Number(raw.compare_at_price) : null
  /** Product-level compare-at is only for simple products; variant pricing uses variant rows. */
  const compareAtPrice =
    hasVariants && variants.length > 0
      ? null
      : capRaw != null && Number.isFinite(capRaw) && capRaw > 0
        ? capRaw
        : null

  const sc = raw.size_chart
  const sizeChart =
    sc &&
    typeof sc === 'object' &&
    (sc.type === 'clothing' || sc.type === 'shoes') &&
    Array.isArray(sc.values)
      ? { type: sc.type, values: sc.values as Record<string, string>[] }
      : null

  return {
    id: raw.id,
    storeId: raw.store_id,
    name: raw.name,
    description,
    slug: raw.slug ?? null,
    images: collectImages(raw),
    hasVariants,
    variants,
    defaultVariantId: raw.default_variant_id ?? null,
    price: Number(raw.price ?? 0),
    stock: Number(raw.stock ?? 0),
    trackInventory: raw.track_inventory !== false,
    isDropshipping,
    status: coerceStatus(raw.status),
    category,
    productType,
    isDigital,
    isService,
    isLocal,
    needsShipping,
    instantDelivery,
    importSource: raw.import_source ?? null,
    digitalFileUrl: raw.digital_file_url ?? null,
    compareAtPrice,
    sizeChart,
  }
}
